'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { ContactForm } from '@/components/blocks/ContactForm';
import { Modal } from '@/components/ui/Modal';
import { getStickyCtaForSlug } from '@/config/stickyCta';
import { cn } from '@/lib/ui/cn';

const SCROLL_THRESHOLD_PX = 200;
const HIDE_NEAR_BOTTOM_PX = 450;

const INNER_CLASS_NAME = cn(
  'pointer-events-auto',
  'flex w-full items-center justify-center',
  'rounded-full px-8 py-3',
  'bg-sh-dark-gray text-white',
  'text-xl font-medium',
  'shadow-[0_4px_10px_0_rgba(41,39,34,0.4)]'
);

export type StickyCTAProps = {
  text: string;
  href?: string;
  onClick?: () => void;
  hidden?: boolean;
};

export function StickyCTA({ text, href, onClick, hidden }: StickyCTAProps) {
  const [visible, setVisible] = useState(false);
  const shown = visible && !hidden;

  useEffect(() => {
    let pending = false;
    const compute = () => {
      pending = false;
      const pastThreshold = window.scrollY > SCROLL_THRESHOLD_PX;
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      const nearBottom = window.scrollY >= maxScroll - HIDE_NEAR_BOTTOM_PX;
      setVisible(pastThreshold && !nearBottom);
    };
    const schedule = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, []);

  return (
    <div
      data-testid='sticky-cta'
      aria-hidden={!shown}
      className={cn(
        'md:hidden',
        'fixed right-0 bottom-0 left-0 z-[50]',
        'w-full p-4',
        'transition-transform duration-300 ease-out',
        'pointer-events-none',
        shown ? 'translate-y-0' : 'translate-y-full'
      )}
    >
      {href ? (
        <Link
          href={href}
          tabIndex={shown ? 0 : -1}
          className={INNER_CLASS_NAME}
        >
          {text}
        </Link>
      ) : (
        <button
          tabIndex={shown ? 0 : -1}
          onClick={onClick}
          className={INNER_CLASS_NAME}
        >
          {text}
        </button>
      )}
    </div>
  );
}

export function StickyCTAForPath() {
  const pathname = usePathname();
  const entry = getStickyCtaForSlug(pathname.split('/').filter(Boolean));
  const [formOpen, setFormOpen] = useState(false);

  if (!entry) return null;

  if ('form' in entry) {
    return (
      <>
        <StickyCTA
          text={entry.text}
          hidden={formOpen}
          onClick={() => setFormOpen(true)}
        />
        <Modal
          isOpen={formOpen}
          onClose={() => setFormOpen(false)}
          contentClassName='overflow-y-auto max-h-[90vh]'
        >
          <ContactForm
            client={process.env.NEXT_PUBLIC_FORMSORT_CLIENT as string}
            flow={process.env.NEXT_PUBLIC_FORMSORT_CONTACT_FORM_FLOW as string}
            variant={
              process.env.NEXT_PUBLIC_FORMSORT_CONTACT_FORM_VARIANT as string
            }
            thankYouPagePath='/contact-us/thank-you'
            onSuccess={() => setFormOpen(false)}
          />
        </Modal>
      </>
    );
  }

  return <StickyCTA text={entry.text} href={entry.href} />;
}
